import { Button, FormGroup, FormLabel } from 'react-bootstrap';
import DynamicField from './DynamicField';
import { Typography } from 'Components/atoms';
import { FormSettings, TypographyVariant } from 'Core-utils/types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document as ContentfulDocument } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import { ChangeEvent, ChangeEventHandler, ReactNode } from 'react';
import { DynamicTab } from 'Components/organisms/Integrations/Integrations';
import classNames from 'classnames';
import { ChipInput } from '../ChipInput';

type DynamicTabContentProps = {
    selectedOptions: FormSettings;
    tab: DynamicTab;
    vendor: any;
    onOptionSelected: (formId: string, name: string, variant?: string) => ChangeEventHandler<HTMLInputElement>;
    onOptionChecked: (formId: string, name: string) => ChangeEventHandler<HTMLInputElement>;
    onButtonClick: (
        formId: string,
        strategy?: string,
        type?: string,
    ) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    hasPlaceholder?: boolean;
    customContent?: Map<string, ReactNode>;
};

export const DynamicTabContent = ({
    selectedOptions,
    tab,
    vendor,
    onOptionSelected,
    onOptionChecked,
    onButtonClick,
    hasPlaceholder = false,
    customContent,
}: DynamicTabContentProps) => {
    const richTextContent =
        tab.type === 'richText' && tab?.contentfulKey ? (vendor as { [k: string]: any })[tab.contentfulKey] : null;
    const richTextDocument =
        richTextContent && typeof richTextContent === 'object' && richTextContent.nodeType === 'document'
            ? (richTextContent as ContentfulDocument)
            : null;

    return (
        <>
            {tab.type === 'text' && tab?.contentfulKey && (vendor as any)?.[tab.contentfulKey] && (
                <Typography variant={(tab.variant as TypographyVariant) || 'body10'}>
                    {(vendor as any)?.[tab.contentfulKey]}
                </Typography>
            )}
            {tab.type === 'richText' && tab?.contentfulKey && richTextDocument && (
                <div style={{ color: 'var(--bs-primary-100)' }}>
                    {documentToReactComponents(richTextDocument, {
                        renderNode: {
                            [BLOCKS.EMBEDDED_ASSET]: (node, _children) => {
                                return (
                                    <img
                                        src={`https://${node.data.target.fields.file.url}`}
                                        height={node.data.target.fields.file.details.image.height}
                                        width={node.data.target.fields.file.details.image.width}
                                        alt={node.data.target.fields.description}
                                    />
                                );
                            },
                        },
                    })}
                </div>
            )}
            {tab.type === 'slackBot' && (
                <a
                    href={`https://slack.com/oauth/v2/authorize?client_id=${process.env.SLACK_CLIENT_ID}&scope=${process.env.SLACK_SCOPE}&user_scope=${process.env.SLACK_USER_SCOPE}`}
                >
                    <img
                        alt="Add to Slack"
                        height="40"
                        width="139"
                        src="https://platform.slack-edge.com/img/add_to_slack.png"
                        srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
                    />
                </a>
            )}
            {tab.type === 'buttonOnly' && (
                <>
                    <Button
                        type="submit"
                        onClick={onButtonClick('', tab.strategy, tab.buttonType)}
                        className="bg-primary-500 text-uppercase caption5 removeButton__button mt-4"
                    >
                        {tab.buttonText}
                    </Button>
                </>
            )}
            {tab.type === 'form' && tab.formId && (
                <>
                    {tab.formTitle && (
                        <Typography
                            variant={(tab.formTitleVariant as TypographyVariant) || 'h4'}
                            color="text-high-emphasis"
                        >
                            {tab.formTitle}
                        </Typography>
                    )}
                    {tab.fields?.map((field) => {
                        // If the field declares a controlling switch via controlledBy,
                        // hide it when that switch's current value is falsy.
                        if (
                            field.controlledBy &&
                            !selectedOptions?.[tab.formId!]?.[field.controlledBy]
                        ) {
                            return null;
                        }
                        const formClassName = classNames({
                            'd-flex justify-content-between mb-2': field.type === 'switch',
                        });
                        return (
                            <FormGroup key={field.name} className={formClassName}>
                                {field.label && (
                                    <FormLabel className="text-text-high-emphasis">{field.label}</FormLabel>
                                )}
                                {field.variant === 'list' ? (
                                    <ChipInput
                                        initialChips={
                                            Array.isArray(selectedOptions[tab.formId!]?.[field.name])
                                                ? (selectedOptions[tab.formId!][field.name] as string[])
                                                : typeof selectedOptions[tab.formId!]?.[field.name] === 'string'
                                                ? [selectedOptions[tab.formId!][field.name] as string]
                                                : []
                                        }
                                        onChange={(updatedChips) => {
                                            const syntheticEvent = {
                                                target: {
                                                    value: updatedChips.join(',').trim(),
                                                },
                                            } as ChangeEvent<HTMLInputElement>;
                                            onOptionSelected(tab.formId!, field.name, field.variant)(syntheticEvent);
                                        }}
                                    />
                                ) : (
                                    <DynamicField
                                        {...field}
                                        formId={tab.formId!}
                                        selectedOptions={selectedOptions}
                                        onOptionChecked={onOptionChecked}
                                        onOptionSelected={onOptionSelected}
                                        hasPlaceholder={hasPlaceholder}
                                    />
                                )}
                            </FormGroup>
                        );
                    })}
                    <Button
                        onClick={onButtonClick(tab.formId, tab.strategy, tab.buttonType)}
                        className="bg-primary-500 text-uppercase caption5 removeButton__button mt-4 p-2"
                        type={tab.buttonType}
                        variant="primary"
                    >
                        {tab.buttonText}
                    </Button>
                </>
            )}
            {tab.type === 'custom' &&
                customContent?.has(tab?.contentfulKey ?? '') &&
                customContent.get(tab.contentfulKey ?? '')}
        </>
    );
};
